import type { NotificationPort } from '@sunucupark/client-core';
import { qk, useSession } from '@sunucupark/client-react';
import { routes } from '@sunucupark/shared/lite';
import type { ConversationSummary, MailFolder } from '@sunucupark/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { inboxFolderIdOf } from './inbox';
import { unreadOf } from './unread';
import { useWebClient } from './use-web-client';
import { contractSchema } from '@/shared/contracts';

/**
 * Tells the reader about mail that arrived while another tab was in front.
 * The hint from the event stream carries no data (ADR 0008), so the notifier
 * waits for the folder list to refresh, notices that the inbox grew and asks
 * the gateway for the one conversation it needs to name.
 *
 * Permission is never requested on its own: the browser prompt belongs to a
 * deliberate action, and the settings page (phase 2c) will offer it. Until
 * then a reader who has already granted it gets the notifications.
 */
export function NewMailNotifier() {
  const client = useWebClient();
  const queryClient = useQueryClient();
  const session = useSession();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const accountId = session?.accountId;
  const lastUnread = useRef<number | null>(null);

  useEffect(() => {
    const notifications = client.platform?.notifications;
    if (!accountId || !notifications) return;

    const stop = notifications.onActivated((target) => {
      if (!target) return;
      void inboxFolderIdOf(client).then((folderId) =>
        navigate({ to: '/mail/$folderId', params: { folderId }, search: { selected: target, view: 'conversation' } }),
      );
    });

    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const folders = queryClient.getQueryData<MailFolder[]>(qk.folders(accountId));
      if (!folders) return;
      const unread = unreadOf(folders);
      const previous = lastUnread.current;
      lastUnread.current = unread;
      // Only a genuine increase is news; the first reading just sets the mark.
      if (previous === null || unread <= previous) return;
      if (!document.hidden) return;
      void announce(notifications, unread - previous);
    });

    async function announce(port: NotificationPort, newMessages: number): Promise<void> {
      if ((await port.permission()) !== 'granted') return;
      const newest = await newestConversation(client, await inboxFolderIdOf(client));
      if (!newest) return;
      await port.show({
        id: newest.id,
        title: newest.participants[0]?.name ?? newest.participants[0]?.email ?? t('app.name'),
        body: newMessages > 1 ? t('mail.newMessages', { count: newMessages }) : newest.subject,
        target: newest.id,
      });
    }

    return () => {
      stop();
      unsubscribe();
    };
  }, [accountId, client, navigate, queryClient, t]);

  return null;
}

/** The conversation at the top of the inbox, or null when it is empty. */
async function newestConversation(client: ReturnType<typeof useWebClient>, folderId: string): Promise<ConversationSummary | null> {
  const answer = await client.api.request({
    path: routes.mail.conversations,
    query: { folderId, limit: 1 },
    schema: contractSchema((contracts) => contracts.ConversationPageSchema),
  });
  return answer.items[0] ?? null;
}
